import React, {useEffect, useMemo, useState} from "react";
import axios from "../axios/axios";
import {
    Box,
    Button,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Input,
    Select,
    SimpleGrid,
    Skeleton,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from "@chakra-ui/react";
import {Link, useParams, useSearchParams} from "react-router-dom";
import formatHoursAndMinutes from "../lib/helpers/formatHoursAndMinutes";
import isValidDateRange from "../lib/helpers/validDateRange";

function getLastFiscalYearRange(date = new Date()) {
    const year = date.getFullYear();
    const fiscalYearEnd = new Date(year, 2, 31);
    const endYear = date > fiscalYearEnd ? year : year - 1;

    return {
        start_date: `${endYear - 1}-04-01`,
        end_date: `${endYear}-03-31`
    };
}

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("fr-FR");
}

function formatMonth(value) {
    if (!value) return "-";
    const [year, month] = String(value).split("-");
    if (!year || !month) return value;

    return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric"
    });
}

function groupRowsByMonth(rows) {
    return rows.reduce((groups, row) => {
        const month = row.period_month || row.month || "Sans mois";
        if (!groups[month]) groups[month] = [];
        groups[month].push(row);
        return groups;
    }, {});
}

function sumMinutes(rows) {
    return rows.reduce((total, row) => total + Number(row.time_spent || 0), 0);
}

export default function AdminUsersDetailsContainer() {
    const {user_id} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const fiscalYearRange = useMemo(() => getLastFiscalYearRange(), []);

    const start_date = useMemo(
        () => searchParams.get("start_date") || fiscalYearRange.start_date,
        [searchParams, fiscalYearRange.start_date]
    );
    const end_date = useMemo(
        () => searchParams.get("end_date") || fiscalYearRange.end_date,
        [searchParams, fiscalYearRange.end_date]
    );
    const id_client = useMemo(
        () => searchParams.get("id_client") || "",
        [searchParams]
    );

    const [user, setUser] = useState(null);
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [totalMinutes, setTotalMinutes] = useState(0);
    const [loading, setLoading] = useState(true);

    const validRange = useMemo(
        () => isValidDateRange(start_date, end_date),
        [start_date, end_date]
    );

    const updateSearchParams = updates => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);

            Object.entries(updates).forEach(([key, value]) => {
                if (value === null || value === undefined || value === "") {
                    next.delete(key);
                } else {
                    next.set(key, String(value));
                }
            });

            return next;
        });
    };

    const applyFiscalYearFilter = () => {
        updateSearchParams(fiscalYearRange);
    };

    useEffect(() => {
        if (!validRange) return;

        const queryParams = new URLSearchParams();
        if (start_date) queryParams.append("start_date", start_date);
        if (end_date) queryParams.append("end_date", end_date);
        if (id_client) queryParams.append("id_client", id_client);

        setLoading(true);

        axios
            .get(`/admin/users/${user_id}/asana-time?${queryParams.toString()}`)
            .then(response => {
                setUser(response.data.user || null);
                setRows(response.data.rows || []);
                setClients(response.data.clients || []);
                setTotalMinutes(Number(response.data.total_minutes || 0));
            })
            .catch(error => {
                console.error("Error fetching user Asana time:", error);
                setRows([]);
                setClients([]);
                setTotalMinutes(0);
            })
            .finally(() => setLoading(false));
    }, [user_id, start_date, end_date, id_client, validRange]);

    const groupedRows = useMemo(() => groupRowsByMonth(rows), [rows]);

    const monthlySummary = useMemo(
        () => Object.entries(groupedRows).map(([month, monthRows]) => ({
            month,
            minutes: sumMinutes(monthRows)
        })),
        [groupedRows]
    );

    return (
        <Box>
            <Stack direction="column" spacing={4}>
                <Breadcrumb spacing="8px">
                    <BreadcrumbItem>
                        <BreadcrumbLink as={Link} to="/admin/users">
                            Collaborateurs
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage>
                        <BreadcrumbLink aria-current="page">
                            <b>
                                {user ? user.name : (
                                    <Skeleton as="span" display="block" height="1em" width="90px"/>
                                )}
                            </b>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage>
                        <BreadcrumbLink aria-current="page">
                            Du {formatDate(start_date)} au {formatDate(end_date)}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>

                <Stack
                    direction={{base: "column", lg: "row"}}
                    width="full"
                    spacing={6}
                    alignItems="start"
                >
                    <Stack width="full" flex="1" minW={0}>
                        <Text fontSize="xl" fontWeight="bold">
                            Temps par mois, client et devis
                        </Text>

                        {loading ? (
                            <Stack spacing={3}>
                                {[...Array(6)].map((_, index) => (
                                    <Skeleton key={index} height="36px"/>
                                ))}
                            </Stack>
                        ) : rows.length === 0 ? (
                            <Text color="red.500">
                                Aucun temps trouve dans cette periode
                            </Text>
                        ) : (
                            Object.entries(groupedRows).map(([month, monthRows]) => (
                                <Box key={month} mb={6}>
                                    <Text fontSize="md" fontWeight="bold" mb={2}>
                                        {formatMonth(month)} - {formatHoursAndMinutes(sumMinutes(monthRows) / 60)}
                                    </Text>

                                    <TableContainer width="full" overflowX="auto">
                                        <Table
                                            variant="simple"
                                            size={{base: "sm", md: "md"}}
                                            sx={{tableLayout: "fixed"}}
                                        >
                                            <Thead>
                                                <Tr>
                                                    <Th width="34%">Client</Th>
                                                    <Th>Devis / section</Th>
                                                    <Th width={{base: "110px", md: "130px"}} isNumeric>
                                                        Temps
                                                    </Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {monthRows.map(row => (
                                                    <Tr key={`${row.period_month || row.month}-${row.client_id}-${row.projet_id}`}>
                                                        <Td overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                            {row.client_name}
                                                            {row.portfolio_name_asana && (
                                                                <Text as="span" fontSize="sm" fontStyle="italic">
                                                                    {" "}({row.portfolio_name_asana})
                                                                </Text>
                                                            )}
                                                        </Td>
                                                        <Td overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                            {row.section_name}
                                                        </Td>
                                                        <Td isNumeric whiteSpace="nowrap">
                                                            {formatHoursAndMinutes(Number(row.time_spent || 0) / 60)}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))
                        )}
                    </Stack>

                    <Stack width={{base: "full", lg: "320px"}} flexShrink={0}>
                        <Text>
                            Total d'heures :{" "}
                            <Text as="span" fontWeight="bold">
                                {formatHoursAndMinutes(totalMinutes / 60)}
                            </Text>
                        </Text>

                        {monthlySummary.length > 0 && (
                            <Stack spacing={1}>
                                <Text fontSize="md" fontWeight="bold">
                                    Recap par mois
                                </Text>
                                {monthlySummary.map(item => (
                                    <Stack
                                        key={item.month}
                                        direction="row"
                                        justifyContent="space-between"
                                        fontSize="sm"
                                    >
                                        <Text>{formatMonth(item.month)}</Text>
                                        <Text fontWeight="bold">
                                            {formatHoursAndMinutes(item.minutes / 60)}
                                        </Text>
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        <Button variant="outline" onClick={applyFiscalYearFilter}>
                            Bilan fiscal {fiscalYearRange.start_date.slice(0, 4)}-
                            {fiscalYearRange.end_date.slice(0, 4)}
                        </Button>

                        <Text fontSize="md" fontWeight="bold">
                            Filtrer par client
                        </Text>
                        <Select
                            placeholder="Tous les clients"
                            value={id_client}
                            onChange={event => updateSearchParams({id_client: event.target.value})}
                        >
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>
                                    {client.client_name}
                                    {client.portfolio_name_asana ? ` (${client.portfolio_name_asana})` : ""}
                                </option>
                            ))}
                        </Select>

                        <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                            <Box>
                                <Text fontSize="md" fontWeight="bold">
                                    Date de debut
                                </Text>
                                <Input
                                    type="date"
                                    value={start_date}
                                    onChange={event =>
                                        updateSearchParams({start_date: event.target.value})
                                    }
                                />
                            </Box>
                            <Box>
                                <Text fontSize="md" fontWeight="bold">
                                    Date de fin
                                </Text>
                                <Input
                                    type="date"
                                    value={end_date}
                                    onChange={event =>
                                        updateSearchParams({end_date: event.target.value})
                                    }
                                />
                            </Box>
                        </SimpleGrid>

                        {!validRange && (
                            <Text color="red.500">
                                La date de fin doit etre posterieure a la date de debut.
                            </Text>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
}
